/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */


/**
 * @author: Kumar Manas
 * @version: $Id$
 */


import _ from 'underscore';
import $ from 'jquery';
import ReactDOM from 'react-dom';
import Report from 'bi-report/src/bi/report/Report';
import AdHocView from 'bi-adhoc/src/bi/adhoc/AdHocView';
import HoverMenu from 'js-sdk/src/common/component/menu/HoverMenu';
import Notification from 'js-sdk/src/common/component/notification/Notification';
import LoadingDialog from 'js-sdk/src/common/component/dialog/LoadingDialog';
import dashboardWiringStandardIds from '../../../enum/dashboardWiringStandardIds';
import dashboardSettings from '../../../dashboardSettings';
import menuContainerTemplate from '../../../template/menuContainerTemplate.htm';
import menuOptionTemplate from '../../../template/menuOptionTemplate.htm';
import jasperserverMessages from '../../../../i18n/jasperserver_messages.properties';
import defaultLinkOptions from '../../../hyperlink/defaultLinkOptions';
import sandboxFactory from '../../../factory/sandboxFactory';
import DashboardBundle from '../../../../i18n/DashboardBundle.properties';
import compTraitUtil from './util/componentTraitUtil';
import hyperlinkTypes from 'bi-report/src/bi/report/jive/enum/hyperlinkTypes';
import WebPageView from "js-sdk/src/common/component/webPageView/WebPageView";
import DashboardExportUtils from '../../../util/DashboardExportUtils';

let i18n = Object.assign({}, jasperserverMessages, DashboardBundle);

/**
 * @memberof adhocTrait
 * @access private
 * @desc checks if adhoc is Ad Hoc Chart
 * @param {object} adhocView
 * @returns {boolean}
 */

function isAdhocChart(adhocView) {
    return adhocView.$("> .dashletContent > .content > .jr-mAdhocViewer > .jr-mAdhoc-visualization > .jr-mVisualization-canvas > .jr-mChartContainer").length > 0;
}

function onReportSuccessfulRun(triggerComponentRendered) {
    this.hideMessage && this.hideMessage();

    triggerComponentRendered !== false && this.trigger("componentRendered", self);

    !compTraitUtil.isAdhocChartReport(this) && compTraitUtil._resetContentOverflow.call(this, compTraitUtil._calculateContentOverflow.call(this));
    toggleVizIcon.call(this);
    this.$el.find('.jrPage .highcharts_parent_container').attr("js-stdnav", "false");

    let isEmpty = !compTraitUtil.findJrPageTable(this.$content);
    isEmpty && this.showMessage({errorCode: "report.empty.error"});
    compTraitUtil.enableBackButton.call(this);

    this.model.reportExecutionId = this.replaceComponent._getExecutionId();
}

function signalHandler(payload) {
    if (payload.name === dashboardWiringStandardIds.REFRESH_SLOT) {
        if (this.paramsModel.paramsChanged) {
            this.paramsModel.paramsChanged = false;
            this.component.params(compTraitUtil.extractExecutionParams.call(this));
        }
        this.refresh();
    }else if (payload.name === dashboardWiringStandardIds.APPLY_SLOT) {
        if (this.paramsModel.paramsChanged) {
            this.paramsModel.paramsChanged = false;
            this.isAdhocRan = false;
            this.paramsModel._current === -1 && this.model.get('dashletHyperlinkTarget') === dashboardSettings.DASHLET_REPLACE_ACTION && this.model.set('isReplacedHyperlink', false);
            let parameters = compTraitUtil.extractExecutionParams.call(this);
            if (parameters.target !== dashboardSettings.DASHLET_REPLACE_ACTION) {
                this.replaceComponent && compTraitUtil.destroyReplaceComponent.call(this);
                if (!this.component) {
                    this.component = compTraitUtil.createAdhocViewInstance.call(this, this.adhocUri, {}, this.model.attributes);
                    this.mapLinkOption();
                }
                this.component.params(parameters);
                this.component.refresh && this.component.refresh();
            }
            this.refresh();
        }
    }
}

function createAdhocReportInstance(resourceUri, params) {
    return (new Report({
        resource: resourceUri,
        params: params,
        server: dashboardSettings.CONTEXT_PATH ? dashboardSettings.CONTEXT_PATH : window.location.origin,
    }));
}

function toggleVizIcon() {
    let $content, showVizSelector = false;
    if (this.model.get('showVizSelectorIcon') !== undefined && this.$el && this.$el.find('.jr-mAdhoc-visualization-launcher').length) {
        showVizSelector = true;
        $content = this.$('> .dashletContent > .content');
        this.model.get('showVizSelectorIcon') ? $content.removeClass("hideVizLauncher") : $content.addClass("hideVizLauncher");
    }
    this.model.set('showVizSelector', showVizSelector);
}

function exportReport(self, options, Notification, i18n, loading) {
    var deferredHandler = (dfd) => {
        dfd.done(function (result) {
            window.open(result.href);
        }).fail(function () {
            Notification.show({
                message: i18n["dashboard.dashlet.error.report.export.failed"]
            });
        }).always(function () {
            loading.close();
            loading.remove();
        });
    }

    if (self.model.drilldownInfo){
        DashboardExportUtils.requestDashboardExport(null, {
            ...options,
            detailed:true,
            contextPath: '../',
            componentModels: self.model.collection.models,
            includeDashletIds: [ self.model.get('id')]
        }).done(function (data) {
            deferredHandler(DashboardExportUtils.wait(null, data.id, new $.Deferred()));
        }).fail(function () {
            loading.close();
            loading.remove();
            Notification.show({
                message: i18n["dashboard.dashlet.error.report.export.failed"]
            });
        })
    } else {
        deferredHandler(self.reportComponent["export"](options))
    }
}

export default {
    _onViewInitialize: function () {
        this.updatedChartType = null;
        this.$el.addClass("dashboardVisualization");
        this.listenTo(this.model, "signal", _.bind(signalHandler, this));
    },
    exportAs: function exportAs(options) {
        let chartComponent, self = this,
            loading = new LoadingDialog({
                cancellable: false
            }),
            dashletHyperlinkType = this.model.get('dashletHyperlinkType'),
            runReportComponent = function () {
                this.reportComponent.run().then(function () {
                    if ((self.updatedChartType && self.component) || (self.replaceChartType && self.replaceComponent)) {
                        chartComponent = self.reportComponent.data().components[0];
                        chartComponent.chartType = self.component ? self.updatedChartType : self.replaceChartType;
                        self.reportComponent.updateComponent(chartComponent).done(function () {
                            exportReport(self, options, Notification, i18n, loading);
                        });
                    } else {
                        exportReport(self, options, Notification, i18n, loading);
                    }
                });
            };
        loading.open();
        if (this.replaceComponent && dashletHyperlinkType === "adhocDataView") {
            this.reportComponent = createAdhocReportInstance.call(this, this.createReplaceReportUri, this.replaceComponent.params());
            runReportComponent.call(this);
        } else if (this.replaceComponent && (dashletHyperlinkType === "reportUnit" || dashletHyperlinkType === "reportOptions")) {
            this.reportComponent = this.replaceComponent;
            exportReport(self, options, Notification, i18n, loading);
        } else {
            this.reportComponent = createAdhocReportInstance.call(this, this.createReportUri, this.component.params());
            runReportComponent.call(this);
        }
    },
    mapLinkOption: function () {
        if (this.dashboardId) {
            var sandbox = sandboxFactory.get(this.dashboardId);
            sandbox.on("linkOptions", compTraitUtil.updateAdhocLinkOptions.call(this));
        }
    },
    /**
     * @memberof adhocTrait
     * @access protected
     * @desc initializes adhoc view component specific behavior
     */

    _initComponent: function () {
        var self = this;
        this.mapLinkOption();
        this.model.getReportResourceUri().done(function (uri, adhocUri, error) {
            if (error) {
                self._broken = true;
                compTraitUtil.onAdhocFailedRun.call(self, error);
            } else {
                self.createReportUri = uri;
                self.adhocUri = adhocUri;
                adhocUri = !(_.isString(adhocUri)) ? self.model.resource.resource.attributes.uri : adhocUri;
                self.component = compTraitUtil.createAdhocViewInstance.call(self, adhocUri, {}, self.model.attributes);
                compTraitUtil.updateAdhocLinkOptions.call(self);
            }
        });
    },

    /**
     * @memberof adhocTrait
     * @access protected
     * @desc remove component, sign off all events remove sub components
     */

    _removeComponent: function (options) {
        var sessionExpired = options ? options.sessionExpired : false;
        ReactDOM.unmountComponentAtNode(this.$content[0]);
        !sessionExpired && this.isAdhocRan && this.component && this.component.destroy();
        !sessionExpired && this.reportComponent && this.reportComponent.destroy();
        this.component = null;
        this.reportComponent = null;
        this.isAdhocRan = false;
        if (this.dashboardId) {
            var sandbox = sandboxFactory.get(this.dashboardId);
            sandbox.off("linkOptions", compTraitUtil.updateAdhocLinkOptions.bind(this));
        }
        this.exportMenu && this.exportMenu.remove();
    },
    _renderComponent: function () {
        if (this.toolbar) {
            var self = this;
            this.exportMenu = new HoverMenu(compTraitUtil.exportOptions, this.toolbar.getOptionView('export').$el, null, {
                menuContainerTemplate: menuContainerTemplate,
                menuOptionTemplate: menuOptionTemplate
            });
            _.each(compTraitUtil.exportOptions, function (option) {
                self.listenTo(self.exportMenu, "option:" + option.action, function () {
                    self.exportMenu.hide();
                    self.exportAs(option.params);
                });
            });
        }
    },
    _resizeComponent: function () {
        if (this.replaceComponent) {
            if (this.replaceComponent instanceof AdHocView) {
                this.replaceComponent.resize();
                compTraitUtil._resetContentOverflow.call(this, compTraitUtil._calculateContentOverflow.call(this));
            } else if (this.replaceComponent instanceof Report) {
                this.replaceComponent.resize();
                !compTraitUtil.isAdhocChartReport(this) && compTraitUtil._resetContentOverflow.call(this, compTraitUtil._calculateContentOverflow.call(this));
            }
        } else if (this.component && this.isAdhocRan) {
            this.component.resize();
        }
    },
    notify: function (parameters, url, target, hasParamGrammer) {
        var signals = _.reduce(this.model.get("outputParameters"), function (memo, param) {
            var value = parameters[param.id];
            if (value) {
                memo || (memo = {});
                memo[param.id] = _.isArray(value) ? value : [value];
            }
            return memo;
        }, false);
        if (signals) {
            // js-60857 below condition is implemented bcz previos selected paramModel values do not get updated if it is not selected
            // so manually updating remaining parameters with undefined.
            _.each(this.model.get("outputParameters"), function(value, key, obj) {
                if(!signals[value.id]){
                    signals[value.id] = [];
                }

            });
            this.model.notify(signals);
            this.parameterBeforeReplace = compTraitUtil.extractExecutionParams.call(this);
            if( target === dashboardSettings.DASHLET_REPLACE_ACTION) {
                compTraitUtil.replaceParameterGrammer.call(this, url, target, hasParamGrammer);
            }
            this.model.collection.getDashboardPropertiesComponent().applyParameters();
        }
    },
    replace: function (replaceInfo) {
        let params = replaceInfo.parameters ? compTraitUtil.convertHyperlinksParamsToVizJsApi(replaceInfo.parameters) : undefined,
            pages,
            self = this;
        this.isAdhocRan = false;
        this.updatedChartType = null;
        this.model.updatedChartType = null;
        compTraitUtil.destroyReplaceComponent.call(this);
        this.paginationView && this.paginationView.hide();
        this.paginationView && this.resizeContentContainer();
        this.model.get('dashletHyperlinkLabel') && this.$dashlet.find('.dashletToolbar.toolbar .header .labelWrapper p').html(this.model.get('dashletHyperlinkLabel'));
        this.model.set('isReplacedHyperlink', true);
        if(this.model.get('showBackButton')){
            this.toolbar.$el.find('.hyperlinkBackbutton').parent().addClass('hyperlink');
            this.toolbar.$el.find('.hyperlink').css('display','block');
        }
        let hyperlinkType = this.model.get('dashletHyperlinkType');
        if (hyperlinkType === "reportUnit") {
            this.replaceComponent = compTraitUtil.createReportInstance.call(this, replaceInfo.resource, params, pages, compTraitUtil.destroyReplaceComponent);
            compTraitUtil.updateReportLinkOptions.call(this);
            this.replaceComponent.run().done(_.bind(onReportSuccessfulRun, this)).fail(_.bind(compTraitUtil.onReportFailedRun, this));
        } else if (hyperlinkType === "adhocDataView") {
            this.model.componentInitializedDfd = new $.Deferred();
            this.model.getReportResourceUri(replaceInfo.resource).done(function (uri, adhocUri, error) {
                if (error) {
                    self._broken = true;
                    compTraitUtil.onAdhocFailedRun.call(self, error);
                } else {
                    self.createReplaceReportUri = uri;
                }
            });
            this.replaceComponent = compTraitUtil.createAdhocViewInstance.call(this, replaceInfo.resource, params, this.model.attributes);
            compTraitUtil.updateAdhocLinkOptions.call(this);
            this.replaceComponent.run().done(_.bind(compTraitUtil.onAdhocSuccessfulRun, this, false, toggleVizIcon)).fail(_.bind(compTraitUtil.onAdhocFailedRun, this));
        } else if (hyperlinkType === "reportOptions") {
            params = Object.keys(params).length === 0  ? self.model.get("dashletHyperlinkReportOptionParam") : params;
            this.replaceComponent = compTraitUtil.createReportInstance.call(this, self.model.get("dashletHyperlinkReportOption"), params, pages, compTraitUtil.destroyReplaceComponent);
            compTraitUtil.updateReportLinkOptions.call(this);
            this.replaceComponent.run().done(_.bind(onReportSuccessfulRun, this)).fail(_.bind(compTraitUtil.onReportFailedRun, this));
        }
    },
    refresh: function () {
        compTraitUtil.updateToolbarBackButton.call(this);
        let self = this,
            res = new $.Deferred();
        if (self.isAdhocRan) {
            self.ready.done(function () {
                self.isAdhocRunning = true;
                self.component
                    .refresh()
                    .fail(res.reject)
                    .done(function (data) {
                        data && self.model.collection && self.model.set({'outputParameters': data.metadata.outputParameters}, {silent: true});
                        compTraitUtil.onAdhocSuccessfulRun.call(self, true, toggleVizIcon);
                        compTraitUtil.isDashletSelect.call(self) && self.$el.trigger('click');
                        res.resolve();
                    });
            });
        } else {
            let parameters = compTraitUtil.extractExecutionParams.call(self);
            if (parameters.target === dashboardSettings.DASHLET_REPLACE_ACTION && !self.model.get('isReplacedHyperlink')) {
                self.component && self._removeComponent();
                self.replace(parameters);
                if (self.toolbar) {
                    self.model.get('showExportButton') && self.toolbar.getOptionView("export").enable();
                    //TODO: remove object with 'menuContainerTemplate' and 'menuOptionTemplate' when designing new JS component
                    self.exportMenu = new HoverMenu(compTraitUtil.exportOptions, self.toolbar.getOptionView('export').$el, null, {
                        menuContainerTemplate: menuContainerTemplate,
                        menuOptionTemplate: menuOptionTemplate
                    });

                    _.each(compTraitUtil.exportOptions, function(option){
                        self.listenTo(self.exportMenu, "option:" + option.action, function(){
                            self.exportMenu.hide();
                            self.exportAs(option.params);
                        });
                    });
                }
            } else if (self.component && parameters.target !== dashboardSettings.DASHLET_REPLACE_ACTION) {
                self.isAdhocRunning = true;
                self.replaceComponent && compTraitUtil.destroyReplaceComponent.call(self);
                self.$content.show();
                self.component
                    .run()
                    .fail(res.reject)
                    .done(function (data) {
                        if (data && self.model.collection){
                            self.model.set({
                                'outputParameters': data.metadata.outputParameters,
                                'initialVisualizationType': data.metadata.currentVisualizationType
                            });
                        }
                        self.isAdhocRan = true;
                        compTraitUtil.onAdhocSuccessfulRun.call(self, true, toggleVizIcon);
                        compTraitUtil.isDashletSelect.call(self) && self.$el.trigger('click');
                        res.resolve();
                    });
            } else if (self.replaceComponent && parameters.target === dashboardSettings.DASHLET_REPLACE_ACTION) {
                self.replaceComponent
                    .run()
                    .fail(res.reject)
                    .done(function () {
                        self.replaceComponent instanceof AdHocView ?
                            compTraitUtil.onAdhocSuccessfulRun.call(self, true, toggleVizIcon) : onReportSuccessfulRun.call(self);
                        res.resolve();
                    });
            } else {
                self._broken || self.hideMessage();
                res.resolve();
            }
        }
        res
            .fail(_.bind(compTraitUtil.onAdhocFailedRun, this))
            .always(_.bind(this._onAdhocRunFinished, this));

        return res;
    },
    _onReportRunFinished: function () {
        this.$el.addClass("rendered");
        this.toolbar && this.toolbar.getOptionView("export").enable();
    },
    back: compTraitUtil.back,
    _refreshPagination: function (totalPages, options) {
        let setOptions = {
            "total": totalPages
        };

        if(options && options.resetCurrent){
            setOptions.current = this.paginationView.model.defaults.current
        }
        // returns pagination validation into default state
        this.paginationView.resetSetOptions();
        this.paginationView.model.set(setOptions);
        this.model.get("showPaginationControl") && this.paginationView.show();
    },
    cancel: function () {
        return (new $.Deferred()).resolve();
    },
    _onComponentRendered: function () {
        this.model.set("isAdhocChart", isAdhocChart(this));
    },
    _onAdhocRunFinished: function () {
        this.isAdhocRunning = false;
        this.$el.addClass("rendered");
        if ((this.isAdhocRan && this.toolbar) || this.model.get('isReplacedHyperlink')) {
            this.toolbar.getOptionView("export").enable();
        }
    }
}
